import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  VStack,
  Heading,
  useToast,
  SimpleGrid,
  Text,
  Flex,
  Icon,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import Navbar from "../components/Navbar";
import CampaignHome from "./CampaignHome";
import { UserAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  FaBuilding,
  FaStore,
  FaBullhorn,
  FaPlus,
  FaCheck,
  FaChevronRight,
  FaChevronDown,
  FaChevronUp,
  FaExclamationCircle,
  FaEdit,
} from "react-icons/fa";

const SectionGrid = ({ title, icon, items, onItemClick, headerAction }) => (
  <Box
    width="100%"
    bg="white"
    p={{ base: 4, sm: 6, md: 8 }}
    borderRadius="xl"
    boxShadow="md"
  >
    <Flex
      justify="space-between"
      align={{ base: "stretch", sm: "center" }}
      direction={{ base: "column", sm: "row" }}
      gap={{ base: 4, sm: 0 }}
      mb={{ base: 6, md: 8 }}
    >
      <HStack spacing={3}>
        <Icon as={icon} boxSize={6} color="black" />
        <Heading as="h2" size="md" color="heading">
          {title}
        </Heading>
      </HStack>
      {headerAction}
    </Flex>

    <SimpleGrid
      columns={{ base: 1, md: 2, lg: 3 }}
      spacing={{ base: 4, md: 6 }}
      width="100%"
    >
      {items.map((item) => (
        <Box
          key={item.id}
          p={{ base: 4, md: 5 }}
          borderRadius="lg"
          border="1px"
          borderColor="gray.200"
          boxShadow="sm"
          cursor="pointer"
          onClick={() => onItemClick(item.id)}
          _hover={{
            transform: "translateY(-2px)",
            transition: "all 0.2s",
            boxShadow: "md",
          }}
          position="relative"
          role="group"
        >
          <Flex justify="space-between" align="center" pr={6}>
            <Heading size="sm" color="heading" fontWeight="medium">
              {item.name}
            </Heading>
            {item.pendingApproval && (
              <Icon
                as={FaExclamationCircle}
                color="orange.500"
                boxSize={{ base: 4, md: 5 }}
                title="Pending Approval"
              />
            )}
          </Flex>
          <Icon
            as={FaChevronRight}
            position="absolute"
            top="50%"
            right={4}
            transform="translateY(-50%)"
            color="gray.400"
            boxSize={{ base: 4, md: 5 }}
            _groupHover={{ color: "gray.600" }}
          />
        </Box>
      ))}
    </SimpleGrid>
  </Box>
);

const EmptyState = ({ onCreateOrganization }) => (
  <Box
    width="100%"
    bg="white"
    p={{ base: 4, sm: 6, md: 8 }}
    borderRadius="xl"
    boxShadow="md"
  >
    <Box p={{ base: 6, md: 8 }} textAlign="center">
      <VStack spacing={{ base: 4, md: 6 }}>
        <Icon as={FaBuilding} boxSize={{ base: 10, md: 12 }} color="black" />
        <Heading size="md" color="heading">
          No Organizations Yet
        </Heading>
        <Text color="gray.600" maxW="md" fontSize={{ base: "sm", md: "md" }}>
          To start a fundraising campaign, you can either create an own
          organization or wait for an invitation to join an existing campaign.
        </Text>
        <Button
          leftIcon={<FaPlus />}
          variant="primary"
          onClick={onCreateOrganization}
          size={{ base: "sm", md: "md" }}
        >
          Create Organization
        </Button>
      </VStack>
    </Box>
  </Box>
);

const ContextSelector = ({
  items,
  selectedId,
  onSelect,
  onAdd,
  addLabel,
  createLabel,
  emptyLabel,
  contextLabel,
  contextIcon,
  fontSize,
  fontWeight,
  color,
  onItemEdit,
  editLabel,
}) => {
  const selectedItem = items.find((i) => i.id === selectedId) || null;
  const hasMultiple = items.length > 1;
  const isEmpty = items.length === 0;

  const displayName = selectedItem?.name || emptyLabel;

  // 0 or 1 item — inline text + small "+" icon to add
  if (!hasMultiple) {
    return (
      <HStack spacing={1} minW={0}>
        <Text
          fontSize={fontSize}
          fontWeight={fontWeight}
          color={isEmpty ? "gray.500" : color}
          fontStyle={isEmpty ? "italic" : "normal"}
          noOfLines={1}
        >
          {displayName}
        </Text>
        {selectedItem?.pendingApproval && (
          <Icon
            as={FaExclamationCircle}
            color="orange.500"
            boxSize={4}
            title="Pending Approval"
          />
        )}
        {!isEmpty && onItemEdit && selectedItem && (
          <IconButton
            aria-label={editLabel || "Edit"}
            title={editLabel || "Edit"}
            icon={<FaEdit />}
            size="xs"
            variant="ghost"
            color="gray.500"
            onClick={() => onItemEdit(selectedItem.id)}
          />
        )}
        {onAdd && (
          <IconButton
            aria-label={addLabel}
            title={addLabel}
            icon={<FaPlus />}
            size="xs"
            variant="ghost"
            color="gray.500"
            onClick={onAdd}
          />
        )}
      </HStack>
    );
  }

  // 2+ items — inline text + chevron, opens a tight dropdown.
  // Edit action sits next to the dropdown (not inside it): user picks the
  // active item via the menu, then clicks the edit icon to open it.
  return (
    <HStack spacing={1} minW={0}>
      <Menu placement="bottom-start" autoSelect={false} isLazy>
        {({ isOpen }) => (
          <>
            <MenuButton
              as={Button}
              variant="ghost"
              size="sm"
              px={1}
              h="auto"
              minH={0}
              py={0.5}
              fontSize={fontSize}
              fontWeight={fontWeight}
              color={color}
              rightIcon={
                <Icon
                  as={isOpen ? FaChevronUp : FaChevronDown}
                  boxSize={2.5}
                  color="gray.500"
                />
              }
              _hover={{ bg: "transparent" }}
              _active={{ bg: "transparent" }}
            >
              <HStack spacing={1.5} minW={0}>
                <Text noOfLines={1}>{displayName}</Text>
                {selectedItem?.pendingApproval && (
                  <Icon
                    as={FaExclamationCircle}
                    color="orange.500"
                    boxSize={3.5}
                  />
                )}
              </HStack>
            </MenuButton>
            <MenuList minW="240px" py={1} zIndex={10}>
              {contextLabel && (
                <Box px={3} pt={2} pb={1}>
                  <HStack spacing={2}>
                    {contextIcon && (
                      <Icon as={contextIcon} boxSize={3} color="gray.500" />
                    )}
                    <Text
                      fontSize="2xs"
                      textTransform="uppercase"
                      color="gray.500"
                      fontWeight="semibold"
                      letterSpacing="wider"
                      noOfLines={1}
                    >
                      {contextLabel}
                    </Text>
                  </HStack>
                </Box>
              )}
              {items.map((item) => {
                const isSelected = item.id === selectedId;
                return (
                  <MenuItem
                    key={item.id}
                    onClick={() => onSelect(item.id)}
                    bg={isSelected ? "blue.50" : undefined}
                    _hover={{ bg: isSelected ? "blue.100" : "gray.50" }}
                    fontWeight={isSelected ? "medium" : "normal"}
                    icon={
                      isSelected ? (
                        <Icon as={FaCheck} boxSize={3} color="blue.600" />
                      ) : (
                        <Box w="12px" />
                      )
                    }
                  >
                    <HStack spacing={2}>
                      <Text fontSize="sm" noOfLines={1}>
                        {item.name}
                      </Text>
                      {item.pendingApproval && (
                        <Icon
                          as={FaExclamationCircle}
                          color="orange.500"
                          boxSize={3}
                          flexShrink={0}
                        />
                      )}
                    </HStack>
                  </MenuItem>
                );
              })}
              {onAdd && createLabel && (
                <>
                  <MenuDivider my={1} />
                  <MenuItem
                    icon={<Icon as={FaPlus} boxSize={3} color="gray.500" />}
                    onClick={onAdd}
                    fontSize="sm"
                    color="gray.700"
                  >
                    {createLabel}
                  </MenuItem>
                </>
              )}
            </MenuList>
          </>
        )}
      </Menu>
      {onItemEdit && selectedItem && (
        <IconButton
          aria-label={editLabel || "Edit"}
          title={editLabel || "Edit"}
          icon={<FaEdit />}
          size="xs"
          variant="ghost"
          color="gray.500"
          onClick={() => onItemEdit(selectedItem.id)}
        />
      )}
    </HStack>
  );
};

const pickName = (raw, language, fallback) => {
  if (!raw) return fallback;
  if (typeof raw === "string") return raw;
  return raw[language] || raw.en || raw.fr || fallback;
};

const Home = () => {
  const [businesses, setBusinesses] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [isTeamMemberOfSelectedCampaign, setIsTeamMemberOfSelectedCampaign] =
    useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = UserAuth();
  const { language } = useLanguage();

  useEffect(() => {
    const fetchAffiliations = async () => {
      if (!user?.uid) return;
      try {
        setLoading(true);
        const businessIds = user.businesses || [];
        const organizationIds = user.organizations || [];
        const campaignIds = user.campaigns || [];

        const [businessDocs, organizationDocs, campaignDocs] =
          await Promise.all([
            Promise.all(
              businessIds.map((id) => getDoc(doc(db, "businesses", id)))
            ),
            Promise.all(
              organizationIds.map((id) =>
                getDoc(doc(db, "organizations", id))
              )
            ),
            Promise.all(
              campaignIds.map((id) => getDoc(doc(db, "campaigns", id)))
            ),
          ]);

        setBusinesses(
          businessDocs
            .filter((snap) => snap.exists())
            .map((snap) => ({
              id: snap.id,
              name: pickName(snap.data().name, language, "Unnamed Business"),
            }))
        );
        setOrganizations(
          organizationDocs
            .filter((snap) => snap.exists())
            .map((snap) => ({
              id: snap.id,
              name: pickName(
                snap.data().name,
                language,
                "Unnamed Organization"
              ),
              pendingApproval: !(snap.data().approved ?? false),
            }))
        );
        setCampaigns(
          campaignDocs
            .filter((snap) => snap.exists())
            .map((snap) => ({
              id: snap.id,
              name: pickName(snap.data().name, language, "Unnamed Campaign"),
              organizationId: snap.data().organizationId || null,
            }))
        );
      } catch (error) {
        console.error("Error fetching user affiliations:", error);
        toast({
          title: "Error",
          description: "Failed to load your dashboard",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAffiliations();
  }, [user, toast, language]);

  // Default the selected org to the first one once orgs load.
  useEffect(() => {
    if (organizations.length === 0) {
      if (selectedOrgId !== null) setSelectedOrgId(null);
      return;
    }
    const stillValid = organizations.some((o) => o.id === selectedOrgId);
    if (!stillValid) {
      setSelectedOrgId(organizations[0].id);
    }
  }, [organizations, selectedOrgId]);

  // Campaigns filtered to the currently-selected org.
  const campaignsForSelectedOrg = useMemo(() => {
    if (!selectedOrgId) return [];
    return campaigns.filter((c) => c.organizationId === selectedOrgId);
  }, [campaigns, selectedOrgId]);

  // When the selected org changes (or campaign list changes), reset the
  // selected campaign to the first one in that org.
  useEffect(() => {
    if (campaignsForSelectedOrg.length === 0) {
      if (selectedCampaignId !== null) setSelectedCampaignId(null);
      return;
    }
    const stillValid = campaignsForSelectedOrg.some(
      (c) => c.id === selectedCampaignId
    );
    if (!stillValid) {
      setSelectedCampaignId(campaignsForSelectedOrg[0].id);
    }
  }, [campaignsForSelectedOrg, selectedCampaignId]);

  // Team members (campaign agents with isAdmin=false) should not see the
  // org-dashboard link, add-org, or add-campaign actions on Home. Mirror
  // the role check CampaignHome uses, scoped to the selected campaign.
  useEffect(() => {
    let cancelled = false;
    if (!user?.uid || !selectedCampaignId) {
      setIsTeamMemberOfSelectedCampaign(false);
      return undefined;
    }
    (async () => {
      try {
        const agentSnap = await getDoc(
          doc(db, `campaigns/${selectedCampaignId}/agents`, user.uid)
        );
        if (cancelled) return;
        setIsTeamMemberOfSelectedCampaign(
          agentSnap.exists() && agentSnap.data().isAdmin !== true
        );
      } catch (err) {
        if (!cancelled) setIsTeamMemberOfSelectedCampaign(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, selectedCampaignId]);

  const handleCreateOrganization = () => {
    navigate("/create-organization");
  };

  const handleCreateCampaign = () => {
    if (!selectedOrgId) return;
    navigate(`/create-campaign/${selectedOrgId}`);
  };

  const hasAnyAffiliation =
    businesses.length > 0 ||
    organizations.length > 0 ||
    campaigns.length > 0;

  return (
    <>
      <Navbar />
      <Box
        minH="calc(100vh - 60px)"
        bg="primaryBackground"
        pt={{ base: "40px", md: "30px" }}
        px={{ base: 4, sm: 6, md: 10 }}
      >
        <VStack
          spacing={{ base: 3, md: 4 }}
          maxWidth="1200px"
          margin="0 auto"
          py={{ base: 6, md: 8 }}
          width="100%"
        >
          <Flex
            width="100%"
            justify="space-between"
            align={{ base: "stretch", md: "flex-start" }}
            direction={{ base: "column", md: "row" }}
            gap={{ base: 4, md: 0 }}
          >
            <VStack
              align={{ base: "center", md: "flex-start" }}
              spacing={0}
              width={{ base: "100%", md: "auto" }}
            >
              <Heading
                as="h1"
                size={{ base: "xl", md: "lg" }}
                color="heading"
                textAlign={{ base: "center", md: "left" }}
                px={{ base: 2, md: 0 }}
              >
                Welcome, {user?.firstName}!
              </Heading>
              {!loading && organizations.length > 0 && (
                <VStack
                  align={{ base: "center", md: "flex-start" }}
                  spacing={0}
                  mt={1}
                >
                  <ContextSelector
                    items={organizations}
                    selectedId={selectedOrgId}
                    onSelect={setSelectedOrgId}
                    onAdd={
                      isTeamMemberOfSelectedCampaign
                        ? undefined
                        : handleCreateOrganization
                    }
                    addLabel="Add another organization"
                    createLabel="Create new organization"
                    onItemEdit={
                      isTeamMemberOfSelectedCampaign
                        ? undefined
                        : (id) => navigate(`/organization/${id}`)
                    }
                    editLabel="Open organization dashboard"
                    fontSize={{ base: "md", md: "lg" }}
                    fontWeight="semibold"
                    color="heading"
                  />
                  <ContextSelector
                    items={campaignsForSelectedOrg}
                    selectedId={selectedCampaignId}
                    onSelect={setSelectedCampaignId}
                    onAdd={
                      selectedOrgId && !isTeamMemberOfSelectedCampaign
                        ? handleCreateCampaign
                        : undefined
                    }
                    addLabel="Add another campaign"
                    createLabel="Create new campaign"
                    emptyLabel="No campaigns yet"
                    contextLabel={
                      organizations.find((o) => o.id === selectedOrgId)?.name
                    }
                    contextIcon={FaBuilding}
                    fontSize={{ base: "sm", md: "md" }}
                    fontWeight="medium"
                    color="gray.600"
                  />
                </VStack>
              )}
            </VStack>
          </Flex>

          {!loading && selectedCampaignId && (
            <CampaignHome
              key={selectedCampaignId}
              campaignId={selectedCampaignId}
              embedded
            />
          )}

          {!loading && businesses.length > 0 && (
            <SectionGrid
              title="Your Businesses"
              icon={FaStore}
              items={businesses}
              onItemClick={(id) => navigate(`/business/${id}/dashboard`)}
            />
          )}

          {!loading && !hasAnyAffiliation && (
            <EmptyState onCreateOrganization={handleCreateOrganization} />
          )}
        </VStack>
      </Box>
    </>
  );
};

export default Home;
